import { useEffect, useMemo, useState } from 'react';
import useDebounce from './useDebounce';
const transformResponse = (response, options) => {
    const { numTermsWithGroupSuggestions, numGroupsSuggestedPerTerm } = options;
    const newSectionsData = {};
    Object.keys(response?.sections || {}).forEach((section) => {
        newSectionsData[section] = [];
        const sectionItems = response.sections[section].map((item) => ({
            ...item,
            id: item?.data?.id,
            section,
        }));
        sectionItems.forEach((item, itemIndex) => {
            newSectionsData[section]?.push(item);
            if (section === 'Search Suggestions' &&
                item?.data?.groups?.length &&
                itemIndex < numTermsWithGroupSuggestions) {
                item.data.groups.forEach((group, groupIndex) => {
                    if (groupIndex < numGroupsSuggestedPerTerm) {
                        const inGroupSearchSuggestion = {
                            ...item,
                            id: `${item.data?.id}_${group.group_id}`,
                            groupName: group.display_name,
                            groupId: group.group_id,
                        };
                        newSectionsData[section]?.push(inGroupSearchSuggestion);
                    }
                });
            }
        });
    });
    return {
        sectionsData: newSectionsData,
        request: response?.request,
        totalNumResultsPerSection: response?.total_num_results_per_section,
    };
};
const useDebouncedFetchSection = (query, cioClient, autocompleteSections, advancedParameters) => {
    // This says sectionsData but it also contain request information
    const [sectionsData, setSectionsData] = useState({
        sectionsData: {},
        request: {},
        totalNumResultsPerSection: {},
    });
    const debouncedSearchTerm = useDebounce(query, advancedParameters?.debounce);
    const { numTermsWithGroupSuggestions = 0, numGroupsSuggestedPerTerm = 0 } = advancedParameters || {};
    const autocompleteParameters = useMemo(() => {
        const decoratedParameters = {
            ...advancedParameters,
        };
        // eslint-disable-next-line no-param-reassign
        delete decoratedParameters?.debounce;
        if (autocompleteSections) {
            decoratedParameters.resultsPerSection = autocompleteSections.reduce((acc, sectionConfig) => ({
                ...acc,
                [sectionConfig.indexSectionName]: sectionConfig?.numResults || 8,
            }), {});
        }
        return decoratedParameters;
    }, [autocompleteSections, advancedParameters]);
    useEffect(() => {
        (async () => {
            const trimmedTerm = debouncedSearchTerm.trim();
            // Do not send a request if the encoded term is longer than 199 characters
            if (trimmedTerm && encodeURIComponent(trimmedTerm).length < 200) {
                try {
                    const response = await cioClient?.autocomplete.getAutocompleteResults(debouncedSearchTerm, autocompleteParameters);
                    if (response) {
                        const newSectionsData = transformResponse(response, {
                            numTermsWithGroupSuggestions,
                            numGroupsSuggestedPerTerm,
                        });
                        setSectionsData(newSectionsData);
                    }
                }
                catch (error) {
                    // eslint-disable-next-line no-console
                    console.log(error);
                }
            }
            else if (!debouncedSearchTerm) {
                setSectionsData({ sectionsData: {}, request: {}, totalNumResultsPerSection: {} });
            }
        })();
    }, [
        debouncedSearchTerm,
        cioClient,
        numTermsWithGroupSuggestions,
        numGroupsSuggestedPerTerm,
        autocompleteParameters,
    ]);
    return sectionsData;
};
export default useDebouncedFetchSection;
